import { forwardRef, type ReactNode } from "react";
import { cn } from "@client/utils/cn";
import { Text } from "@sfc/ui";

/** Shared page hierarchy; callers supply their own navigation, metadata and actions. */
export const PageHeader = forwardRef<
  HTMLElement,
  {
    title?: ReactNode;
    titleClassName?: string;
    description?: ReactNode;
    navigation?: ReactNode;
    navigationActions?: ReactNode;
    actions?: ReactNode;
    children?: ReactNode;
    sticky?: boolean;
    className?: string;
  }
>(function PageHeader(
  {
    title,
    titleClassName,
    description,
    navigation,
    navigationActions,
    actions,
    children,
    sticky = false,
    className,
  },
  ref,
) {
  const hasNavigation = Boolean(navigation || navigationActions);

  return (
    <header
      ref={ref}
      className={cn(
        "flex flex-col gap-2",
        hasNavigation ? "pt-4" : "pt-8",
        sticky && "sticky top-0 z-10 border-b border-border bg-background pb-4",
        className,
      )}
    >
      {hasNavigation && (
        <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-2">
          {navigation}
          {navigationActions}
        </div>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          {title && (
            <h1
              className={cn(
                "heading-xl [overflow-wrap:anywhere]",
                titleClassName,
              )}
            >
              {title}
            </h1>
          )}
          {children}
          {description && (
            <Text variant="body-md" tone="muted-foreground">
              {description}
            </Text>
          )}
        </div>
        {actions && (
          <div className="flex max-w-sm flex-col gap-2 sm:items-end sm:text-right">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
});
